import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Users, UserPlus, MessageSquare, Trash2, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PeopleSearch from "@/components/PeopleSearch";
import PresenceAvatar from "@/components/PresenceAvatar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewChatDialog({ open, onOpenChange, onOpenConversation }) {
  const [contacts, setContacts] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadContacts = () => axios.get(`${API}/chat/contacts`).then(r => setContacts(r.data || [])).catch(() => {});
  useEffect(() => { if (open) { loadContacts(); setSelected([]); setGroupName(""); } }, [open]);

  const addContact = async (u) => {
    try { await axios.post(`${API}/chat/contacts`, { user_id: u.user_id }); toast.success(`${u.name} adicionado aos contatos`); loadContacts(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const removeContact = async (id) => { await axios.delete(`${API}/chat/contacts/${id}`).catch(() => {}); loadContacts(); };
  const openDirect = async (uid) => {
    try { const r = await axios.post(`${API}/chat/conversations/direct`, { user_id: uid }); onOpenChange(false); onOpenConversation(r.data.conversation_id); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const createGroup = async () => {
    if (!groupName.trim() || selected.length === 0) { toast.error("Dê um nome e escolha ao menos 1 membro"); return; }
    setBusy(true);
    try { const r = await axios.post(`${API}/chat/groups`, { name: groupName.trim(), member_ids: selected }); onOpenChange(false); onOpenConversation(r.data.conversation_id); toast.success("Grupo criado"); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro ao criar grupo"); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="new-chat-dialog">
        <DialogHeader><DialogTitle>Nova conversa</DialogTitle></DialogHeader>
        <Tabs defaultValue="contacts">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="contacts" data-testid="tab-contacts">Contatos</TabsTrigger>
            <TabsTrigger value="add" data-testid="tab-add-contact">Adicionar</TabsTrigger>
            <TabsTrigger value="group" data-testid="tab-new-group">Novo grupo</TabsTrigger>
          </TabsList>
          <TabsContent value="contacts" className="space-y-1 max-h-80 overflow-y-auto" data-testid="contacts-list">
            {contacts.length === 0 && <div className="text-sm text-muted-foreground p-4 text-center">Nenhum contato ainda. Use "Adicionar" para buscar pessoas por nome ou usuário.</div>}
            {contacts.map(c => (
              <div key={c.user_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted" data-testid={`contact-${c.user_id}`}>
                <PresenceAvatar src={c.picture} name={c.name} online={c.online} className="w-9 h-9" />
                <div className="flex-1 min-w-0"><div className="text-sm font-semibold truncate">{c.name}</div><div className="text-[11px] text-muted-foreground">{c.online ? "online" : "offline"}</div></div>
                <Button size="sm" className="rounded-full gap-1" data-testid={`contact-chat-${c.user_id}`} onClick={() => openDirect(c.user_id)}><MessageSquare className="w-3.5 h-3.5" /> Conversar</Button>
                <Button size="icon" variant="ghost" className="text-red-500" data-testid={`contact-remove-${c.user_id}`} onClick={() => removeContact(c.user_id)} aria-label="Remover"><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </TabsContent>
          <TabsContent value="add">
            <PeopleSearch autoFocus showActions={false} placeholder="Nome ou usuário..." onSelect={addContact} />
            <p className="text-[11px] text-muted-foreground mt-2 flex items-center gap-1"><UserPlus className="w-3 h-3" /> Clique numa pessoa para adicionar aos contatos.</p>
          </TabsContent>
          <TabsContent value="group" className="space-y-3">
            <Input data-testid="group-name-input" placeholder="Nome do grupo" value={groupName} onChange={(e) => setGroupName(e.target.value)} maxLength={60} />
            <div className="text-xs text-muted-foreground">Selecione membros ({selected.length}/99)</div>
            <div className="max-h-56 overflow-y-auto space-y-1" data-testid="group-members-picker">
              {contacts.length === 0 && <div className="text-sm text-muted-foreground p-3 text-center">Adicione contatos primeiro.</div>}
              {contacts.map(c => (
                <button key={c.user_id} type="button" data-testid={`group-pick-${c.user_id}`} onClick={() => toggle(c.user_id)}
                  className={`w-full flex items-center gap-3 p-2 rounded-lg border text-left ${selected.includes(c.user_id) ? "border-primary bg-primary/5" : "border-transparent hover:bg-muted"}`}>
                  <PresenceAvatar src={c.picture} name={c.name} online={c.online} className="w-8 h-8" />
                  <span className="text-sm flex-1 truncate">{c.name}</span>
                  {selected.includes(c.user_id) && <Check className="w-4 h-4 text-primary" />}
                </button>
              ))}
            </div>
            <Button className="w-full rounded-full gap-2" data-testid="create-group-btn" disabled={busy} onClick={createGroup}><Users className="w-4 h-4" /> Criar grupo</Button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
